import React, { useEffect, useRef, useState } from "react";

import MenuIcon from "@mui/icons-material/Menu";
import TuneIcon from "@mui/icons-material/Tune";
import { Box, IconButton, Popover, Stack } from "@mui/material";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { Menu } from "components/popups";
import Highcharts from "highcharts";
import HighchartsReact, { HighchartsReactRefObject } from "highcharts-react-official";
import { useDevice } from "hooks";
import { useActionHook } from "hooks/shared-hooks/use-action-hook";
import { useDealActions } from "store";
import { IInvestmentDetailField, ITransformedChartConfig } from "store/types";

import { ChartMenu } from "./ChartMenu";

interface ChartProps {
  reqConfig: ITransformedChartConfig;
  columns: IInvestmentDetailField[];
  isNeedRefetch: boolean;
  invalidateRefetch: () => void;
  dealId: number;
  isFreemium?: boolean;
}

export const Chart: React.FC<ChartProps> = ({
  reqConfig,
  columns,
  isNeedRefetch,
  invalidateRefetch,
  dealId,
  isFreemium = false
}) => {
  const { chartName, groupBy, valueBy } = reqConfig;
  const { getInvestmentDetailsChartAction } = useDealActions();
  const { isMobile } = useDevice();
  if (!dealId) return null;

  const { data, loading, action: getChartData } = useActionHook(getInvestmentDetailsChartAction);
  const { data: chartData, title, axis, valueSuffix, valuePrefix } = data || {};

  useEffect(() => {
    if (dealId) {
      getChartData({ dealId, chartName, groupBy, valueBy });
    }
  }, [dealId, chartName, groupBy, valueBy]);

  useEffect(() => {
    if (!isNeedRefetch) return;
    if (dealId) {
      getChartData({ dealId, chartName, groupBy, valueBy });
      invalidateRefetch();
    }
  }, [isNeedRefetch]);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const graphColors: string[] = [
    "#4690F5",
    "#32CED8",
    "#D428BD",
    "#496682",
    "#6E45EB",
    "#EBAF01",
    "#D93E3E",
    "#D77108",
    "#0ABB91",
    "#FEA458"
  ];

  const chartRef = useRef<HighchartsReactRefObject>(null);
  const defaultOptions: Highcharts.Options = {
    chart: {
      type: "pie",
      height: 227,
      marginTop: 30,
      alignTicks: false,
      borderRadius: 16,
      ...(isMobile && {
        marginBottom: 70 // Add space for legend
      }),
      events: {
        load: function () {
          const centerX = isMobile ? "50%" : "40%";
          const centerY = isMobile ? "38%" : "50%";
          this.series[0].update({
            type: "pie",
            center: [centerX, centerY]
          });

          this.legend.allItems.forEach((item: any) => {
            const tooltipRoot = document.body;
            const element = item.legendItem.label.element;

            element.addEventListener("mouseover", () => {
              const tooltip = document.createElement("div");
              tooltip.className = "richText-inline-measure-tooltip";
              tooltip.textContent = item.name;

              // Append tooltip temporarily to measure its dimensions
              tooltip.style.visibility = "hidden";
              tooltip.style.position = "fixed";
              tooltipRoot.appendChild(tooltip);

              const rect = element.getBoundingClientRect();
              const tooltipRect = tooltip.getBoundingClientRect();
              const viewportWidth = window.innerWidth;
              const viewportHeight = window.innerHeight;

              // Calculate available space in each direction
              const spaceAbove = rect.top;
              const spaceBelow = viewportHeight - rect.bottom;
              const spaceLeft = rect.left;
              const spaceRight = viewportWidth - rect.right;

              // Determine best position based on available space
              const tooltipWidth = tooltipRect.width;
              const tooltipHeight = tooltipRect.height;
              const spacing = 8; // Spacing between anchor and tooltip

              let top = 0;
              let left = 0;

              // Priority: bottom > top > right > left (but choose based on available space)
              if (spaceBelow >= tooltipHeight + spacing) {
                // Position below
                top = rect.bottom + spacing;
                left = rect.left + rect.width / 2 - tooltipWidth / 2;
              } else if (spaceAbove >= tooltipHeight + spacing) {
                // Position above
                top = rect.top - tooltipHeight - spacing;
                left = rect.left + rect.width / 2 - tooltipWidth / 2;
              } else if (spaceRight >= tooltipWidth + spacing) {
                // Position to the right
                top = rect.top + rect.height / 2 - tooltipHeight / 2;
                left = rect.right + spacing;
              } else if (spaceLeft >= tooltipWidth + spacing) {
                // Position to the left
                top = rect.top + rect.height / 2 - tooltipHeight / 2;
                left = rect.left - tooltipWidth - spacing;
              } else {
                // Fallback: position below, but adjust to fit viewport
                top = rect.bottom + spacing;
                left = rect.left + rect.width / 2 - tooltipWidth / 2;
              }

              // Ensure tooltip stays within viewport bounds
              top = Math.max(0, Math.min(top, viewportHeight - tooltipHeight));
              left = Math.max(0, Math.min(left, viewportWidth - tooltipWidth));

              tooltip.style.top = `${top}px`;
              tooltip.style.left = `${left}px`;
              tooltip.style.visibility = "visible";
            });

            element.addEventListener("mouseout", () => {
              const tooltip = document.body.querySelector(".richText-inline-measure-tooltip");
              if (!tooltip) return;
              tooltip.remove();
            });
          });
        }
      }
    },
    title: {
      text: title,
      align: "left",
      style: {
        fontWeight: "500",
        fontSize: "18px",
        fontFamily: "Roboto",
        color: "#071F35"
      }
    },
    tooltip: {
      valuePrefix: valuePrefix || "",
      valueSuffix: valueSuffix || ""
    },
    series: [
      {
        name: axis || "",
        type: "pie",
        data: chartData,
        dataLabels: {
          enabled: !isMobile, // Hide on mobile, show on desktop
          formatter: function (this: any) {
            // Don't show percentage if it's 0%
            if (Math.round(this.percentage) === 0 || this.y === 0) {
              return "";
            }
            return `${Math.round(this.percentage)}%`;
          },
          distance: 10,
          style: {
            fontSize: "14px",
            fontWeight: "500",
            color: "#071F35",
            textOutline: "none"
          },
          connectorWidth: 0 // Remove connector lines
        },
        showInLegend: true,
        size: isMobile ? 100 : 112,
        innerSize: "60%" // Makes it a donut chart
      }
    ],
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        borderWidth: 1, // 1px spacing between slices
        borderColor: "#f5f5f5" // Match background color for spacing effect
      }
    },
    credits: {
      enabled: false
    },
    legend: {
      align: "center",
      verticalAlign: isMobile ? "bottom" : "middle",
      layout: isMobile ? "horizontal" : "vertical",
      x: isMobile ? 0 : 150,
      itemStyle: {
        maxWidth: "150px",
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap"
      },
      labelFormatter: function (this: any) {
        const name = this.name || "";
        const percentage = this.percentage ? Math.round(this.percentage) : 0;
        const nameWithPercentage = `${name} - ${percentage}%`;
        // Estimate: average character width is ~8-9px at 14px font size
        // For 150px max width, approximately 18-20 characters
        const maxChars = 18;
        if (nameWithPercentage.length > maxChars) {
          // Truncate name but keep percentage visible
          const availableChars = maxChars - (percentage.toString().length + 2); // +2 for " %"
          const truncatedName = name.length > availableChars ? name.substring(0, availableChars - 3) + "..." : name;
          return `${truncatedName} - ${percentage}%`;
        }
        return nameWithPercentage;
      },
      ...(isMobile &&
        isFreemium && {
          // Configure for 2-3 items per row with scroll
          itemWidth: 110, // Width per item to show ~2-3 per row on mobile
          maxHeight: 60, // Limit height to show 2 rows max
          y: -5, // Position legend closer to chart to reduce pushing
          itemStyle: {
            fontSize: "11px",
            maxWidth: "110px",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden"
          },
          labelFormatter: function (this: any) {
            const name = this.name || "";
            const percentage = this.percentage ? Math.round(this.percentage) : 0;
            const maxLength = 10;
            const truncatedName = name.length > maxLength ? name.substring(0, maxLength) + "..." : name;
            return `${truncatedName} - ${percentage}%`;
          },
          navigation: {
            activeColor: "#4690F5",
            inactiveColor: "#92A7BC",
            animation: true,
            arrowSize: 12
          },
          itemDistance: 8,
          rtl: false,
          enabled: true,
          floating: false
        })
      // verticalAlign: "middle",
      // layout: "vertical",
      // x: 150,
      // labelFormatter: function () {
      //   const name = this.name || "";
      //   // Escape HTML to prevent XSS
      //   const escapeHtml = (text: string) => {
      //     return typeof text === "string" // It was a crash, the server sent number instead of string.
      //       ? text
      //       : String(text)
      //           .replace(/&/g, "&amp;")
      //           .replace(/</g, "&lt;")
      //           .replace(/>/g, "&gt;")
      //           .replace(/"/g, "&quot;")
      //           .replace(/'/g, "&#039;");
      //   };
      //   const escapedName = escapeHtml(name);
      //   return `<div style="
      //     display: inline-block;
      //     max-width: 200px;
      //     overflow: hidden;
      //     text-overflow: ellipsis;
      //     white-space: nowrap;
      //     vertical-align: middle;
      //   ">${escapedName}</div>`;
      // },
      // useHTML: true
    },
    colors: graphColors,
    exporting: {
      enabled: false
    },
    navigation: {
      buttonOptions: {
        enabled: false
      }
    }
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleExport = (type: string) => {
    (chartRef.current?.chart as any)?.exportChartLocal({ type });
  };

  return (
    <Box
      className="chart"
      sx={{
        flexGrow: 1,
        position: "relative",
        borderRadius: "8px",
        backgroundColor: "#ffffff",
        ...(isFreemium && {
          border: "1px solid #dbe8f5"
        }),
        ...(!isFreemium && {
          boxShadow: "0 1px 2px 0 #1212171a"
        }),
        "&:hover .chart-option": {
          opacity: "1 !important"
        },
        "& .highcharts-container": {
          display: "flex !important",
          justifyContent: "flex-start",
          alignItems: "center"
        },
        "& .highcharts-legend-item text": {
          maxWidth: "150px !important",
          textOverflow: "ellipsis !important",
          overflow: "hidden !important",
          whiteSpace: "nowrap !important",
          display: "inline-block !important"
        }
      }}
    >
      <AsyncContentMui isLoading={loading} boxProps={{ sx: { height: isFreemium ? 227 : 285 } }}>
        <HighchartsReact
          highcharts={Highcharts}
          ref={chartRef}
          options={{
            ...defaultOptions,
            colors: graphColors,
            chart: {
              ...defaultOptions.chart,
              marginLeft: 0,
              marginRight: 0
            }
          }}
        />
        <Stack
          className="menu-trigger-blocks"
          sx={{
            position: "absolute",
            top: "10px",
            right: "10px",
            flexDirection: "row",
            gap: 1
          }}
        >
          {!isFreemium && (
            <IconButton
              className="chart-option"
              sx={{
                opacity: anchorEl ? 1 : 0,
                transition: "opacity 0.3s linear"
              }}
              id={"pie-chart-" + name}
              onClick={handleOpenMenu}
            >
              <TuneIcon />
            </IconButton>
          )}
          <Menu
            iconButtonProps={{ className: "chart-option", sx: { opacity: 0, transition: "opacity 0.3s linear" } }}
            keepTriggerEnabled
            icon={<MenuIcon />}
            options={[
              {
                title: "View in full screen",
                onClick: () => (chartRef.current?.chart as any).fullscreen.toggle()
              },
              {
                title: "Print chart",
                onClick: () => (chartRef.current?.chart as any).print(),
                hasLine: true
              },
              {
                title: "Download PNG image",
                onClick: () => handleExport("image/png")
              },
              {
                title: "Download JPEG image",
                onClick: () => handleExport("image/jpeg")
              },
              {
                title: "Download PDF Document",
                onClick: () => handleExport("application/pdf")
              },
              {
                title: "Download SVG vector image",
                onClick: () => handleExport("image/svg+xml"),
                hasLine: true
              },
              {
                title: "Download CSV",
                onClick: () => (chartRef.current?.chart as any).downloadCSV()
              },
              {
                title: "Download XLS",
                onClick: () => (chartRef.current?.chart as any).downloadXLS()
              }
            ]}
          />
        </Stack>
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleCloseMenu}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right"
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right"
          }}
        >
          <Stack sx={{ p: "15px", boxShadow: "sm", minWidth: "350px", gap: "10px" }}>
            <ChartMenu columns={columns} defaultValue={reqConfig} onClose={handleCloseMenu} />
          </Stack>
        </Popover>
      </AsyncContentMui>
    </Box>
  );
};
